import {
  GoogleMap,
  Marker,
  useJsApiLoader,
  InfoWindow,
} from "@react-google-maps/api";
import { useEffect, useState, useLayoutEffect, useCallback } from "react";
import ActivityMarkerIcon from "../../assets/activityMarker.svg";
import EventMarkerIcon from "../../assets/eventMarker.svg";
import PlaceMarkerIcon from "../../assets/placeMarker.svg";
import Card from "../Card/Card";
import LocationIcon from "../../assets/location.svg";
import styles from "./Map.module.css";
import Modal from "../Modal/Modal";
import DefaultImage from "../../assets/defaultImage.svg";

const LIBRARIES = ["places"];

const Map = ({ locations, center, setCenter, setApiRequest }) => {
  const [cardDetails, setCardDetails] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [mapref, setMapRef] = useState(null);
  // const [center, setCenter] = useState(center);
  const [mapBound, setMapBound] = useState(null);
  const [showMapCard, setShowmapCard] = useState(false);

  const initialCardStatus = {
    activities: Array(locations.activities.length).fill(false),
    events: Array(locations.events.length).fill(false),
    places: Array(locations.places.length).fill(false),
  };
  const [showCard, setShowCard] = useState(initialCardStatus);

  const { isLoaded } = useJsApiLoader({
    googleMapsApiKey: process.env.REACT_APP_GOOGLE_MAP_PLACES_API_KEY,
    libraries: LIBRARIES,
  });
  const containerStyle = {
    width: "100%",
    height: "100vh",
    overflow: "hidden",
  };

  useEffect(() => {
    getCurrentLocation();
  }, [mapref]);

  const getCurrentLocation = () => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          setCenter((prev) => ({
            ...prev,
            lat: position.coords.latitude,
            lng: position.coords.longitude,
            current_latitude: position.coords.latitude,
            current_longitude: position.coords.longitude,
          }));
          setApiRequest((prev) => ({
            ...prev,
            latitude: position.coords.latitude,
            longitude: position.coords.longitude,
            current_latitude: position.coords.latitude,
            current_longitude: position.coords.longitude,
          }));
        },
        (error) => {
          setCenter((prev) => ({
            ...prev,
            lat: 39.952583,
            lng: -75.165222,
            current_latitude: 39.952583,
            current_longitude: -75.165222,
          }));
          setApiRequest((prev) => ({
            ...prev,
            latitude: 39.952583,
            longitude: -75.165222,
            current_latitude: 39.952583,
            current_longitude: -75.165222,
          }));
        }
      );
    }
  };

  const onMarkerClicked = (marker, type, id, index) => {
    const selectedCard = locations[type].find((item) => item.id === id);
    const updated = Array(showCard[type].length).fill(false);
    setCardDetails(selectedCard);
    setShowmapCard(true);
    updated[index] = true;
    setShowCard((prev) => ({
      ...prev,
      [type]: [...updated],
    }));
  };

  const handleOnLoad = (map) => {
    setMapRef(map);
  };

  let timeOut;
  const handleCenterChanged = () => {
    clearTimeout(timeOut);
    timeOut = setTimeout(() => {
      changeCenter();
    }, 700);
  };

  const changeCenter = useCallback(() => {
    if (mapref) {
      const newCenter = mapref.getCenter();
      setCenter((prev) => ({
        ...prev,
        lat: newCenter.lat(),
        lng: newCenter.lng(),
      }));
      setMapBound((prev) => ({
        ...prev,
        lat: mapref.getBounds()?.getNorthEast().lat(),
        lng: mapref.getBounds()?.getNorthEast().lng(),
      }));
    }
  }, [mapref]);

  useLayoutEffect(() => {
    window.addEventListener("resize", changeCenter);
    return () => window.removeEventListener("resize", changeCenter);
  }, [mapref, changeCenter]);

  useEffect(() => {
    if ((!center.lat && !center.lng) || !mapBound) {
      return;
    }
    setCardDetails(null);
    setShowmapCard(false);
    const R = 3958.8; // Radius of the Earth in miles
    const rlat1 = center.lat * (Math.PI / 180); // Convert degrees to radians
    const rlat2 = mapBound?.lat * (Math.PI / 180); // Convert degrees to radians
    const difflat = rlat2 - rlat1; // Radian difference (latitudes)
    const difflon = (mapBound?.lng - center.lng) * (Math.PI / 180); // Radian difference (longitudes)

    const d1 =
      2 *
      R *
      Math.asin(
        Math.sqrt(
          Math.sin(0) * Math.sin(0 / 2) +
            Math.cos(rlat1) *
              Math.cos(rlat2) *
              Math.sin(difflon / 2) *
              Math.sin(difflon / 2)
        )
      );
    const d2 =
      2 *
      R *
      Math.asin(
        Math.sqrt(
          Math.sin(difflat / 2) * Math.sin(difflat / 2) +
            Math.cos(rlat1) *
              Math.cos(rlat2) *
              Math.sin(0 / 2) *
              Math.sin(0 / 2)
        )
      );
    setApiRequest((prev) => ({
      ...prev,
      latitude: center.lat,
      longitude: center.lng,
      distance: mapBound ? Math.min(d1, d2) : 15,
    }));
  }, [mapBound, center, setApiRequest]);

  return (
    <div>
      {isLoaded ? (
        <GoogleMap
          mapContainerStyle={containerStyle}
          center={{ lat: center.lat, lng: center.lng }}
          zoom={11}
          options={{
            streetViewControl: false,
            mapTypeControl: false,
            fullscreenControl: false,
            zoomControl: false,
            minZoom: 4,
          }}
          onLoad={handleOnLoad}
          onClick={() => setShowCard(initialCardStatus)}
          onDragEnd={handleCenterChanged}
          onZoomChanged={handleCenterChanged}
        >
          <Marker
            position={{
              lat: center.current_latitude,
              lng: center.current_longitude,
            }}
          />
          {locations.activities.map((activity, i) => (
            <Marker
              key={activity.id}
              position={{
                lat: activity.location.latitude,
                lng: activity.location.longitude,
              }}
              icon={ActivityMarkerIcon}
              onClick={(e) => onMarkerClicked(e, "activities", activity.id, i)}
            >
              {showMapCard && showCard["activities"][i] && (
                <InfoWindow
                  position={{
                    lat: activity.location.latitude,
                    lng: activity.location.longitude,
                  }}
                >
                  <div className={styles.cardContainer}>
                    <div
                      className={styles.imageContainer}
                      style={{
                        backgroundImage: activity.image
                          ? `url(${activity.image})`
                          : `url(${DefaultImage})`,
                      }}
                    ></div>
                    <div className={styles.cardDetails}>
                      <p className={styles.cardName}>{activity.name}</p>
                      <p className={styles.locationName}>
                        {activity.location.name
                          ? activity.location.name
                          : activity.address}
                      </p>
                      <div className={styles.distanceContainer}>
                        <img src={LocationIcon} alt="" />
                        <p className={styles.cardDistance}>
                          {activity.distance_from_location} Mi away
                        </p>
                      </div>
                      <button
                        type="button"
                        className={styles.button}
                        onClick={() => setShowModal(true)}
                      >
                        View Details
                      </button>
                    </div>
                  </div>
                </InfoWindow>
              )}
            </Marker>
          ))}
          {locations.events.map((event, i) => (
            <Marker
              key={event.id}
              position={{
                lat: event.location.latitude,
                lng: event.location.longitude,
              }}
              icon={EventMarkerIcon}
              onClick={(e) => onMarkerClicked(e, "events", event.id, i)}
            >
              {showMapCard && showCard["events"][i] && (
                <InfoWindow
                  position={{
                    lat: event.location.latitude,
                    lng: event.location.longitude,
                  }}
                >
                  <div className={styles.cardContainer}>
                    <div
                      className={styles.imageContainer}
                      style={{
                        backgroundImage: event.image
                          ? `url(${event.image})`
                          : `url(${DefaultImage})`,
                      }}
                    ></div>
                    <div className={styles.cardDetails}>
                      <p className={styles.cardName}>{event.name}</p>
                      <p className={styles.locationName}>
                        {event.location.name
                          ? event.location.name
                          : event.address}
                      </p>
                      <div className={styles.distanceContainer}>
                        <img src={LocationIcon} alt="" />
                        <p className={styles.cardDistance}>
                          {event.distance_from_location} Mi away
                        </p>
                      </div>
                      <button
                        type="button"
                        className={styles.button}
                        onClick={() => setShowModal(true)}
                      >
                        View Details
                      </button>
                    </div>
                  </div>
                </InfoWindow>
              )}
            </Marker>
          ))}
          {locations.places.map((place, i) => (
            <Marker
              key={place.id}
              position={{
                lat: place.location.latitude,
                lng: place.location.longitude,
              }}
              icon={PlaceMarkerIcon}
              onClick={(e) => onMarkerClicked(e, "places", place.id, i)}
            >
              {showMapCard && showCard["places"][i] && (
                <InfoWindow
                  position={{
                    lat: place.location.latitude,
                    lng: place.location.longitude,
                  }}
                >
                  <div className={styles.cardContainer}>
                    <div
                      className={styles.imageContainer}
                      style={{
                        backgroundImage: place.image
                          ? `url(${place.image})`
                          : `url(${DefaultImage})`,
                      }}
                    ></div>
                    <div className={styles.cardDetails}>
                      <p className={styles.cardName}>{place.name}</p>
                      <p className={styles.locationName}>
                        {place.location.name
                          ? place.location.name
                          : place.address}
                      </p>
                      <div className={styles.distanceContainer}>
                        <img src={LocationIcon} alt="" />
                        <p className={styles.cardDistance}>
                          {place.distance_from_location} Mi away
                        </p>
                      </div>
                      <button
                        type="button"
                        className={styles.button}
                        onClick={() => setShowModal(true)}
                      >
                        View Details
                      </button>
                    </div>
                  </div>
                </InfoWindow>
              )}
            </Marker>
          ))}
        </GoogleMap>
      ) : (
        <></>
      )}
      {cardDetails && <Card cardDetails={cardDetails} />}
      <Modal showModal={showModal} setShowModal={setShowModal} />
    </div>
  );
};

export default Map;
